// Miroir de backend/surfaces.py — calculs Façades / Sols / Toitures (précision conservée, arrondi à l'affichage).
import { parseFr } from "@/lib/helpers";

export const SURFACE_PRESTS = ["facades", "sols", "toitures"];
export const SHAPES = {
  facades: { rectangle: [["largeur", "Largeur horizontale"], ["hauteur", "Hauteur"]], triangle: [["largeur", "Base"], ["hauteur_pignon", "Hauteur du pignon"]] },
  sols: { rectangle: [["longueur", "Longueur"], ["largeur", "Largeur"]], triangle: [["base", "Base"], ["hauteur", "Hauteur"]] },
  toitures: { rectangle: [["longueur", "Longueur"], ["largeur", "Largeur"]], triangle: [["base", "Base"], ["hauteur", "Hauteur du triangle"]],
    trapeze: [["grande_base", "Grande base"], ["petite_base", "Petite base"], ["hauteur", "Hauteur du trapèze"]] },
};
export const FORM_LABELS = { rectangle: "Rectangle", triangle: "Triangle / pignon", trapeze: "Trapèze", composee: "Forme composée", saisie: "Surface saisie" };
export const ORIGIN_LABELS = { calculee: "Calculée", saisie: "Saisie manuellement", estimee: "Estimée", a_confirmer: "À confirmer", a_completer: "À compléter" };
export const ROOF_TYPES = { plate: "Toiture plate", un_pan: "Toiture à un pan", deux_pans: "Toiture à deux pans", quatre_pans: "Toiture à quatre pans", complexe: "Toiture complexe / autre" };

const num = (v) => { const n = parseFr(v); return n === null || n < 0 ? null : n; };

function shapeArea(prest, forme, d) {
  if (forme === "saisie") { const s = num(d.surface); return [s, s === null ? ["surface"] : []]; }
  const keys = (SHAPES[prest]?.[forme] || []).map(([k]) => k);
  if (!keys.length) return [null, ["forme"]];
  const v = keys.map((k) => num(d[k]));
  const missing = keys.filter((_, i) => v[i] === null);
  if (missing.length) return [null, missing];
  if (forme === "rectangle") return [v[0] * v[1], []];
  if (forme === "triangle") return [(v[0] * v[1]) / 2, []];
  return [((v[0] + v[1]) * v[2]) / 2, []];
}

export function computeSurface(prest, l = {}, roofType) {
  const forme = l.forme || "rectangle";
  let missing = []; const notes = []; let brute;
  if (forme === "composee") {
    const parts = l.parts || [];
    brute = parts.length ? 0 : null;
    if (!parts.length) missing.push("parties");
    parts.forEach((p, i) => { const [a, m] = shapeArea(prest, p.forme || "rectangle", p); if (a === null) missing = missing.concat(m.map((x) => `partie ${i + 1} : ${x}`)); else brute += a; });
  } else { const [a, m] = shapeArea(prest, forme, l); brute = a; missing = missing.concat(m); }
  let projected = null;
  if (prest === "toitures" && roofType !== "plate" && l.mesure === "horizontal" && brute !== null) {
    projected = brute;
    const p = num(l.pente);
    if (p === null || !['deg', 'pct'].includes(l.pente_unite)) { brute = null; notes.push("Dimensions horizontales uniquement : surface réelle à confirmer (pente ou unité de pente non renseignée)"); }
    else { const ang = l.pente_unite === "deg" ? (p * Math.PI) / 180 : Math.atan(p / 100); brute = ang < (89 * Math.PI) / 180 ? projected / Math.cos(ang) : null; notes.push("Surface rampante calculée depuis la pente saisie"); }
  }
  let ded = 0;
  (l.exclusions || []).forEach((e, i) => {
    if (!e.deduire) return;
    const a = num(e.largeur), b = num(e.hauteur), q = num(e.quantite) || 1;
    if (a === null || b === null) missing.push(`exclusion ${i + 1}`); else ded += a * b * q;
  });
  let nette = null;
  if (brute !== null) { nette = brute - ded; if (nette < 0) { notes.push("Déductions supérieures à la surface brute : nette ramenée à 0, à vérifier"); nette = 0; } }
  const origin = l.fiabilite || (brute === null && projected !== null ? "a_confirmer" : brute === null ? "a_completer" : forme === "saisie" ? "saisie" : "calculee");
  return { brute, deduite: ded, nette, projected, missing, complete: nette !== null && !missing.length, origin, notes };
}

export function surfaceTotals(zones, prest) {
  let total = 0, partial = false; const todo = [];
  zones.filter((z) => z.prestation === prest && z.inclus !== false).forEach((z) => {
    const r = computeSurface(prest, z.lignes?.[0] || {}, z.roof_type);
    if (r.nette === null || !r.complete) { partial = true; todo.push(z.name || "Zone sans nom"); }
    if (r.nette !== null) total += r.nette;
  });
  return { total, partial, todo };
}

export const fmt2 = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

const formeOf = (p) => p.lignes?.[0]?.forme;
export function roofLayout(pans, type, W = 360, H = 150) {
  const x0 = 70, y0 = 25, w = W - 140, h = H - 50, ym = y0 + h / 2;
  if (type === "quatre_pans" && pans.length >= 4) {
    const tr = pans.filter((p) => formeOf(p) === "trapeze"), tg = pans.filter((p) => formeOf(p) === "triangle");
    if (tr.length >= 2 && tg.length >= 2) {
      const r1 = x0 + h / 2, r2 = x0 + w - h / 2;
      const shapes = [
        { zone: tr[0], pts: [[x0, y0], [x0 + w, y0], [r2, ym], [r1, ym]], c: [x0 + w / 2, y0 + h / 4] },
        { zone: tr[1], pts: [[x0, y0 + h], [x0 + w, y0 + h], [r2, ym], [r1, ym]], c: [x0 + w / 2, y0 + (3 * h) / 4] },
        { zone: tg[0], pts: [[x0, y0], [x0, y0 + h], [r1, ym]], c: [x0 + h / 6, ym] },
        { zone: tg[1], pts: [[x0 + w, y0], [x0 + w, y0 + h], [r2, ym]], c: [x0 + w - h / 6, ym] }];
      const used = new Set(shapes.map((s) => s.zone));
      return [shapes, pans.filter((p) => !used.has(p))];
    }
  }
  if (type === "deux_pans" && pans.length >= 2) {
    return [[{ zone: pans[0], pts: [[x0, y0], [x0 + w, y0], [x0 + w, ym], [x0, ym]], c: [x0 + w / 2, y0 + h / 4] },
      { zone: pans[1], pts: [[x0, ym], [x0 + w, ym], [x0 + w, y0 + h], [x0, y0 + h]], c: [x0 + w / 2, y0 + (3 * h) / 4] }], pans.slice(2)];
  }
  const n = Math.min(pans.length, 4);
  return [pans.slice(0, n).map((p, i) => { const a = x0 + (i * w) / n, b = x0 + ((i + 1) * w) / n; return { zone: p, pts: [[a, y0], [b, y0], [b, y0 + h], [a, y0 + h]], c: [(a + b) / 2, ym] }; }), pans.slice(n)];
}

export function roofLabel(z) {
  const r = computeSurface("toitures", z.lignes?.[0] || {}, z.roof_type);
  const sure = r.complete && !["estimee", "a_confirmer"].includes(r.origin);
  if (r.nette === null) return { r, sure, text: r.origin === "a_confirmer" ? "à confirmer" : "à compléter" };
  return { r, sure, text: `${fmt2(r.nette)} m²${sure ? "" : " (à confirmer)"}` };
}
